import { useQueryClient } from '@tanstack/react-query';
import { FileUIPart } from 'ai';
import { useRef, useState } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { isRachelChatsListQueryKey } from '../../../services/modules/rachelChats/rachelChats.keys';
import { useCreateRachelChat } from '../../../services/modules/rachelChats/rachelChats.mutations';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useRachelChatContext } from '../contexts/Rachel.context';
import rachelStore, { useRachelStore } from '../store/rachel.store';
import { useRachelErrorReporter } from './useRachelErrorReporter';
import {
  ShouldIgnoreSubmitParams,
  UseRachelSubmitOptions,
  UseRachelSubmitReturn,
} from './useRachelSubmit.types';
import { ensureActiveChat, runSingleFlight } from './useRachelSubmit.utils';

const shouldIgnoreSubmit = ({ text, consumeFiles, isSending }: ShouldIgnoreSubmitParams) =>
  (!text && !consumeFiles) || isSending;

export const useRachelSubmit = ({
  shouldStartNewChat = false,
  pageContext,
}: UseRachelSubmitOptions = {}): UseRachelSubmitReturn => {
  const {
    attachCurrentPage,
    detachPageContext,
    sendMessage,
    startNewConversation,
    replacementPageContext,
    isPageContextDismissed,
  } = useRachelChatContext();
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const createRachelChat = useCreateRachelChat();
  const { reportRachelError } = useRachelErrorReporter();
  const ensureActiveChatPromiseRef = useRef<Promise<boolean> | null>(null);
  const isSendingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ensureActiveChatForSend = () =>
    ensureActiveChat({
      getActiveChatId: () => useRachelStore.getState().getActiveChatIdForTeam(team?.id),
      setActiveChatId: (chatId) =>
        useRachelStore.getState().setActiveChatId(chatId, team?.id ?? null),
      createChat: () => createRachelChat.mutateAsync(),
      invalidateChatsList: () =>
        void queryClient.invalidateQueries({
          predicate: (query) => isRachelChatsListQueryKey(query.queryKey),
        }),
    });

  const getActiveChatId = () => useRachelStore.getState().getActiveChatIdForTeam(team?.id);

  const attachPageToCreatedChat = async () => {
    const chatId = getActiveChatId();
    if (!chatId) {
      throw new Error('chat ativo não encontrado após criação');
    }

    try {
      await attachCurrentPage(chatId, pageContext);
    } catch (error) {
      if (getActiveChatId() === chatId) {
        useRachelStore.getState().clearActiveChat();
      }
      throw error;
    }
  };

  const refreshPageContextIfMoved = async (): Promise<boolean> => {
    const chatId = getActiveChatId();
    if (!replacementPageContext || !chatId) {
      return true;
    }

    try {
      await attachCurrentPage(chatId);
      return true;
    } catch (error) {
      reportRachelError(error, LangEnum.RACHEL_PAGE_CONTEXT_UPDATE_FAILED);
      return false;
    }
  };

  const detachDismissedPageContext = async (hasCreatedChat: boolean): Promise<boolean> => {
    const chatId = getActiveChatId();
    if (hasCreatedChat || !chatId) {
      return true;
    }

    try {
      await detachPageContext(chatId);
      return true;
    } catch (error) {
      reportRachelError(error, LangEnum.RACHEL_PAGE_CONTEXT_UPDATE_FAILED);
      return false;
    }
  };

  const syncPageContext = async (hasCreatedChat: boolean): Promise<boolean> => {
    if (isPageContextDismissed && !pageContext) {
      return detachDismissedPageContext(hasCreatedChat);
    }

    if (hasCreatedChat) {
      await attachPageToCreatedChat();
      return true;
    }

    return refreshPageContextIfMoved();
  };

  const prepareChatForSend = async (): Promise<boolean> => {
    if (shouldStartNewChat) {
      await startNewConversation();
    }
    const hasCreatedChat = await runSingleFlight(
      ensureActiveChatPromiseRef,
      ensureActiveChatForSend,
    );
    const hasPageContext = await syncPageContext(hasCreatedChat);
    if (!hasPageContext) {
      return false;
    }
    return true;
  };

  const prepareChatForSendSafely = async (): Promise<boolean> => {
    try {
      return await prepareChatForSend();
    } catch (error) {
      reportRachelError(error, LangEnum.RACHEL_CHAT_NOT_SAVED);
      return false;
    }
  };

  const submit = async (text: string, consumeFiles?: () => FileUIPart[]): Promise<boolean> => {
    if (shouldIgnoreSubmit({ text, consumeFiles, isSending: isSendingRef.current })) {
      return false;
    }
    isSendingRef.current = true;
    setIsSubmitting(true);
    rachelStore.open();

    const isPrepared = await prepareChatForSendSafely();
    isSendingRef.current = false;
    setIsSubmitting(false);
    if (!isPrepared) {
      return false;
    }

    const files = consumeFiles?.();
    if (!text && !files?.length) {
      return false;
    }

    sendMessage({ text, files });
    return true;
  };

  return { submit, isSubmitting };
};
